import { useTranslation } from "react-i18next";
import useCrudFilters, { CrudFilters } from "../../../../hooks/useCrudFilters";
import { relationSchema } from "../../../relation/domain/models/RelationModel";
import { EntitySelectField } from "../../../general/fields/modules/commons/entitySelectField/entitySelectField";
import { requestTypeLists } from "../../domain/lists/requestTypeLists";
import { LicencePlateSelectField } from "../../../general/fields/modules/commons/licencePlateSelectField/licencePlateSelectField";
import { ReactSelectOptionType } from "../../../../shared/types/reactSelectType";
import { Button, Form } from "react-bootstrap";
import {
  TableFilterInputWrapper,
  TableFilters,
} from "../../../general/table/modules/commons/tableFilters/tableFilters";
import { TableFiltersWrapper } from "../../../general/table/modules/commons/tableFilters/tableFilters";
import Select from "react-select";

export const RequestTableFilters = ({
  updateFilter,
  removeFilter,
  getFilter,
  filters,
  resetFilters,
  removeFilters,
  updateFilters,
}: CrudFilters) => {
  const { t } = useTranslation("common");

  const typeValue = getFilter("tireset.type");
  const licencesValue = getFilter("tireset.car.licence", "in");
  const vinValue = getFilter("tireset.car.vin", "contains") as string;
  const additionalInfoValue = getFilter(
    "tireset.car.additional_info",
    "contains",
  ) as string;
  const relationValue = getFilter("relation.id");
  const requestTypeValue = getFilter("type");
  const requestStateValue = getFilter("state", "in");
  const freeStockValue = getFilter("freestock");
  const pickupAndDeliveryDateValue = getFilter("pickup_and_delivery_date");
  const inOutStorageDateValue = getFilter("in_out_storage");

  const relationFilters = useCrudFilters({
    searchBy: "SearchByRootCustomer",
    fields: {},
  });

  const requestTypeListsOptions: { label: string; value: string }[] =
    requestTypeLists.map((type) => {
      return { value: type, label: t(type) };
    });

  let requestStateValueOptions: { label: string; value: string | number }[] =
    [];
  if (Array.isArray(requestStateValue)) {
    requestStateValueOptions = requestStateValue.map((type) => {
      return { value: type, label: t(type + "") };
    });
  }

  return (
    <TableFiltersWrapper>
      <TableFilters>
        <TableFilterInputWrapper md={8}>
          <label>{t("Licence plates")}</label>
          <LicencePlateSelectField
            onChange={(options: unknown) => {
              const selectedOptions = options as ReactSelectOptionType[];
              if (options === null || selectedOptions.length === 0) {
                removeFilter("tireset.car.licence");
                return;
              }

              const selectedOptionsStringArray = selectedOptions.map(
                (option) => {
                  return option.label.replace(/-/g, "").toUpperCase();
                },
              );

              updateFilter(
                "tireset.car.licence",
                selectedOptionsStringArray,
                "in",
              );
            }}
            value={licencesValue}
          />
        </TableFilterInputWrapper>
        <TableFilterInputWrapper md={4}>
          <label>{t("Search by car")}</label>
          <Form.Control
            type="text"
            placeholder={t("Search by car")}
            value={vinValue ?? additionalInfoValue ?? ""}
            onChange={(event) => {
              const value = event.target.value;
              if (value === "") {
                removeFilters([
                  {
                    key: "tireset.car.vin",
                  },
                  {
                    key: "tireset.car.additional_info",
                  },
                  {
                    key: "tireset.car.licence",
                  },
                ]);
              } else {
                updateFilters([
                  {
                    key: "tireset.car.vin",
                    value,
                    type: "contains",
                    condition: "or",
                  },
                  {
                    key: "tireset.car.additional_info",
                    value,
                    type: "contains",
                    condition: "or",
                  },
                  {
                    key: "tireset.car.licence",
                    value: value.replace(/-/g, "").toUpperCase(),
                    type: "contains",
                    condition: "or",
                  },
                ]);
              }
            }}
          />
        </TableFilterInputWrapper>
        <TableFilterInputWrapper md={3}>
          <label>{t("Dealer")}</label>
          <EntitySelectField
            entity={"Relation"}
            prefix={"relations"}
            onChange={(option) => {
              if (option == null) {
                removeFilter("relation.id");
                relationFilters.removeFilter("relation.id");
                return;
              }

              const { value } = option as { value: string };
              updateFilter("relation.id", value);
              relationFilters.updateFilter("relation.id", value);
            }}
            limit={100}
            clearable={true}
            schema={relationSchema}
            value={relationValue}
          />
        </TableFilterInputWrapper>
        <TableFilterInputWrapper>
          <label>{t("Pickup and delivery date")}</label>
          <Form.Control
            type={"date"}
            value={`${pickupAndDeliveryDateValue ?? ""}`}
            onChange={(event) => {
              updateFilter("pickup_and_delivery_date", event.target.value);
            }}
          />
        </TableFilterInputWrapper>
        <TableFilterInputWrapper>
          <label>{t("In/out storage")}</label>
          <Form.Control
            type={"date"}
            value={`${inOutStorageDateValue ?? ""}`}
            onChange={(event) => {
              updateFilter("in_out_storage", event.target.value);
            }}
          />
        </TableFilterInputWrapper>
        <TableFilterInputWrapper>
          <label>{t("Request")}</label>
          <Form.Select
            onChange={(event) => {
              const value = event.target.value;
              if (value === "") {
                removeFilter("type");
              } else {
                updateFilter("type", event.target.value);
              }
            }}
            value={`${requestTypeValue ?? ""}`}
          >
            <option value={""}>{"- " + t("All") + " -"}</option>
            <option value={"deliver"}>{t("Deliver")}</option>
            <option value={"pickup"}>{t("Pickup")}</option>
          </Form.Select>
        </TableFilterInputWrapper>
        <TableFilterInputWrapper>
          <label>{t("Type band")}</label>
          <Form.Select
            onChange={(event) => {
              const value = event.target.value;
              if (value === "") {
                removeFilter("tireset.type");
              } else {
                updateFilter("tireset.type", event.target.value);
              }
            }}
            value={`${typeValue ?? ""}`}
          >
            <option value={""}>{"- " + t("All") + " -"}</option>
            <option value={"summer"}>{t("Summer")}</option>
            <option value={"winter"}>{t("Winter")}</option>
            <option value={"all_season"}>{t("All season")}</option>
          </Form.Select>
        </TableFilterInputWrapper>
        <TableFilterInputWrapper>
          <label>{t("Free stock")}</label>
          <Form.Select
            onChange={(event) => {
              const value = event.target.value;
              if (value === "") {
                removeFilter("freestock");
              } else {
                updateFilter("freestock", event.target.value === "1");
              }
            }}
            value={`${freeStockValue ?? ""}`}
          >
            <option value={""}>{"- " + t("All") + " -"}</option>
            <option value={"1"}>{t("Yes")}</option>
            <option value={"0"}>{t("No")}</option>
          </Form.Select>
        </TableFilterInputWrapper>
        <TableFilterInputWrapper md={12}>
          <label>{t("State")}</label>
          <Select
            noOptionsMessage={() => {
              return t("No options");
            }}
            onChange={(options: unknown) => {
              const selectedOptions = options as ReactSelectOptionType[];

              const selectedOptionsStringArray = selectedOptions.map(
                (option) => {
                  return option.value;
                },
              );

              selectedOptionsStringArray.length > 0
                ? updateFilter("state", selectedOptionsStringArray, "in")
                : removeFilter("state");
            }}
            value={requestStateValueOptions}
            isMulti={true}
            options={requestTypeListsOptions}
            className={"entity-select"}
            classNamePrefix={"entity-select"}
          ></Select>
        </TableFilterInputWrapper>
      </TableFilters>

      {filters?.fields && Object.keys(filters.fields).length > 0 && (
        <Button
          onClick={resetFilters}
          size={"sm"}
          className="reset-filters"
          variant="light"
        >
          {t("Reset filters")}
        </Button>
      )}
    </TableFiltersWrapper>
  );
};
